// @flow
import React, { useMemo, useCallback, useRef } from 'react';
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import { defaults } from 'ol/control';
import makeStyles from '@material-ui/core/styles/makeStyles';
import type { MapSource } from '../../types';
import { ZINDEX } from '../../constants/appConstants';
import { Map as OlMap, layers } from '../ui/openlayers';
// import DebugMap from '../ui/openlayers/DebugMap';
import mapService from '../../services/map';
import Targets from './Targets';
import Zones from './Zones';

const ZOOM_THROTTLING_INTERVAL = 500;

const defaultView = {
    center: [1587674.20, 6464620.47],
    // center: [1915981.897, 6135193.144],
    zoom: 15,
    // minZoom: 12,
    maxZoom: 21
};
const getSource = (mapSource: MapSource) => {
    let source;
    if(mapSource) {
        if(mapSource.type === 'xyz') {
            source = <layers.Tile source={new XYZ({ url: mapSource.url})} zIndex={ZINDEX.map} />;
        }
        else if(mapSource.type === 'osm') {
            source = <layers.Tile source={new OSM()} zIndex={ZINDEX.map} />;
        }
        else if(mapSource.type === 'mapbox') {
            source = <layers.Mapbox url={mapSource.url} accessToken={mapSource.accessToken} zIndex={ZINDEX.map} />;
        }
        else {
            throw new Error(`Unknown map source type ${mapSource.type}`);
        }
    }

    let view = defaultView;

    if(mapSource.view) {
        view = {
            ...defaultView,
            ...mapSource.view
        };
    }

    const map = mapService.hasMap() && mapService.getMap();
    const oldView = map && map.getView();

    if(oldView) {
        const oldViewOpts = {
            zoom: oldView.getZoom(),
            center: oldView.getCenter()
        };

        view = {
            ...view,
            ...oldViewOpts
        };
    }

    return {
        source,
        view
    };
};



const useStyles = makeStyles({
    root: {
        height: 'calc(100vh - 64px)'
    },
    zoom: {
        top: '0.5em',
        right: '0.5em'
    }
});

export type Props = $ReadOnly<{
    mapSource: MapSource,
    drawingEnabled: boolean,
    throttlingInterval: number,

    hideSidebar: () => void,
    setThrottlingInterval: (interval: number) => void
}>;
function Map({ mapSource, drawingEnabled, throttlingInterval, hideSidebar, setThrottlingInterval }: Props) {
    const classes = useStyles();
    const throttlingRef = useRef({});
    const { view, source } = useMemo(() => getSource(mapSource), [mapSource]);
    // return (<div className="map"><DebugMap /></div>);

    throttlingRef.current.interval = throttlingInterval;

    const getMapRef = useCallback((map) => {
        mapService.setMap(map);
    }, []);

    const controls = useMemo(() => (
        defaults({
            zoomOptions: {
                className: classes.zoom
            }
        })
    ), [classes.zoom]);

    const onMapClick = useCallback((e) => {
        if(!drawingEnabled) {
            const map = mapService.getMap();

            if(map.hasFeatureAtPixel(e.pixel)) {
                setTimeout(() => {
                    const filter = (feature) => {
                        const type = feature.get('type');
                        return type === 'target' || type === 'draggableLabel';
                    };
                    if(!mapService.hasSelected(filter)) {
                        hideSidebar();
                    }
                }, 300);
            }
            else {
                setTimeout(() => hideSidebar(), 300);
            }
        }
    }, [drawingEnabled, hideSidebar]);

    const handleZoom = useCallback(() => {
        if(throttlingRef.current.interval < ZOOM_THROTTLING_INTERVAL) {
            setThrottlingInterval(ZOOM_THROTTLING_INTERVAL);
        }

        if(throttlingRef.current.timerId) {
            clearTimeout(throttlingRef.current.timerId);
        }

        throttlingRef.current.timerId = setTimeout(() => setThrottlingInterval(0), ZOOM_THROTTLING_INTERVAL);
    }, [setThrottlingInterval]);

    return (
        <div className={classes.root}>
            <OlMap
                mapRef={getMapRef}
                view={view}
                controls={controls}
                onClick={onMapClick}
                onZoom={handleZoom}
            >
                {source}

                <Zones />
                <Targets />
            </OlMap>
        </div>
    );
}

export default Map;